import {
  AsyncButton,
  Badge,
  Separator,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
  showErrorToast,
  showSuccessToast,
} from '@bpinternal/ui-kit-next'
import { useSuspenseInfiniteQuery } from '@tanstack/react-query'
import { Undo2, UserRound } from 'lucide-react'
import { Boundary, UserBadge } from '~/components'
import { DataListItem, DataListRoot } from '~/elements'
import { ConversationSummary } from '~/features/conversations/components/ConversationSummary'
import { useConversationParticipants, useCurrentUserWorkspaceMember } from '~/hooks'
import { listConversationsInfiniteQuery } from '~/queries'
import { useSuspenseQuery } from '~/services'
import { cn } from '~/utils'
import { useCloseConversation, useSetAssignee } from '../hooks'
import { trackEvent } from '~/providers'
import { useNavigate } from '@tanstack/react-router'
import { Conversation } from '@botpress/hitl-client'

type Props = {
  className?: string
  currentConversation: Conversation
  botId: string
  workspaceId: string
}

export const ConversationDetails = ({ className, currentConversation, botId, workspaceId, ...props }: Props) => {
  const navigate = useNavigate()

  const { mutate: setAssignee, isPending: isSettingAssignee } = useSetAssignee({
    workspaceId,
    botId,
    onSuccess: () => {
      showSuccessToast('Assignee updated')

      trackEvent({
        type: 'hitl_conversation_assigned',
        agentId: currentConversation.assignee?.workspaceMemberId,
        patientId: currentConversation.requester?.id,
        conversationId: currentConversation.id,
      })
    },
    onError: () => {
      showErrorToast('Failed to update assignee')
    },
  })
  const { mutate: closeConversation, isPending: isSolvingTicket } = useCloseConversation({
    workspaceId,
    botId,
    onSuccess: () => {
      showSuccessToast('Conversation closed')

      trackEvent({
        type: 'hitl_conversation_closed',
        agentId: assignee?.id,
        agentEmail: assignee?.email,
        patientId: shownParticipants[0]!.id,
        conversationId: currentConversation.id,
      })

      navigate({ to: '/workspaces/$workspaceId/bots/$botId/hitl', params: { botId, workspaceId } })
    },
    onError: () => {
      showErrorToast('Failed to close Conversation')
    },
  })

  const participants = useConversationParticipants({
    conversationId: currentConversation.id,
    botId,
    workspaceId,
  }).data

  const workspaceMembers = useSuspenseQuery('workspaces_/$workspaceId_/members', { workspaceId }).data
  const currentWorkspaceMember = useCurrentUserWorkspaceMember({ workspaceId })!

  const assigneeId = currentConversation.assignee?.workspaceMemberId
  const assignee = workspaceMembers?.find((member) => member.id === assigneeId)

  const shownParticipants = participants?.slice(0, 10) ?? []
  const hiddenParticipants = participants?.slice(10) ?? []

  const isAssignedToMe = assigneeId === currentWorkspaceMember.id

  const conversationWithIntegrationName = {
    channel: '',
    integration: 'hitl',
    ...currentConversation,
  }

  return (
    <div className="flex flex-col gap-4">
      <DataListRoot orientation={'vertical'} {...props} className={cn(className)}>
        <DataListItem label="Conversation">
          <ConversationSummary
            conversation={conversationWithIntegrationName}
            botId={botId}
            workspaceId={workspaceId}
            className={'w-full'}
          />
        </DataListItem>
        <DataListItem label="Participants">
          <div className="flex flex-wrap gap-2">
            {shownParticipants.map((participant) => (
              <UserBadge
                key={participant.id}
                user={{
                  id: participant.id,
                  name: participant.name ?? 'Unknown User',
                  pictureUrl: participant.pictureUrl,
                }}
                className="w-fit"
              />
            ))}
            {hiddenParticipants.length > 0 && <Badge variant="outline">+ {hiddenParticipants.length} more</Badge>}
          </div>
        </DataListItem>
        {shownParticipants[0] && (
          <Boundary>
            <ConversationsWithSameParticipant
              botId={botId}
              workspaceId={workspaceId}
              participantId={shownParticipants[0].id} //this is not ideal, we assume that the 1st participant is the main one
              currentConversationId={currentConversation.id}
            />
          </Boundary>
        )}
        {assignee && (
          <DataListItem label="Assigned">
            <UserBadge
              user={{
                id: assignee.id,
                name: assignee.displayName ?? 'Unknown User',
                pictureUrl: assignee.profilePicture,
              }}
              className="w-fit"
            />
          </DataListItem>
        )}
      </DataListRoot>
      <Separator />

      {currentConversation.status === 'assigned' && isAssignedToMe && (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <AsyncButton
                onClick={() => closeConversation({ conversationId: currentConversation.id })}
                loading={isSolvingTicket}
              >
                <span>Resume Chatbot</span>
                <Undo2 className="h-4 w-4" />
              </AsyncButton>
            </TooltipTrigger>
            <TooltipContent>
              <p>This will mark the conversation as solved and will handover the control back to chatbot</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )}
      {currentConversation.status === 'pending' && (
        <AsyncButton
          disabled={isSettingAssignee}
          variant="secondary"
          className="bg-grass-9 text-white hover:bg-grass-8"
          onClick={() => {
            setAssignee({ conversationId: currentConversation.id, workspaceMemberId: currentWorkspaceMember.id })
          }}
          loading={isSettingAssignee}
        >
          <UserRound className="h-4 w-4" />
          <span>Assign to me</span>
        </AsyncButton>
      )}
    </div>
  )
}

const ConversationsWithSameParticipant = ({
  botId,
  workspaceId,
  participantId,
  currentConversationId,
}: {
  botId: string
  workspaceId: string
  participantId: string
  currentConversationId: string
}) => {
  const conversationsWithSameParticipant = useSuspenseInfiniteQuery(
    listConversationsInfiniteQuery({ botId, workspaceId, participantIds: [participantId] })
  )
    .data.pages.flatMap((page) => page.conversations)
    .filter((conv) => conv.id !== currentConversationId && conv.integration !== 'hitl')
    .sort((a, b) => b.updatedAt.localeCompare(a.updatedAt))
    .slice(0, 5)

  return (
    conversationsWithSameParticipant.length > 0 && (
      <DataListItem label="More conversations from user">
        <div className={'flex min-w-0 flex-col gap-2'}>
          {conversationsWithSameParticipant.map((conv) => (
            <ConversationSummary key={conv.id} conversation={conv} botId={botId} workspaceId={workspaceId} />
          ))}
        </div>
      </DataListItem>
    )
  )
}
